import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import { Container } from '../DefaultStyles/Details';
import {
    Body,
    Content,
    DefaultRepImageURL,
    Details,
    DetailsContainer,
    Footer,
    FooterSpan,
    Title
} from '../DefaultStyles/Shared';

import { RepPosition, RepsWrapper } from '../../components/RepStance/Components';

import WidgetHeader from '../Widget/WidgetHeader';
import Overlay from '../Widget/Overlay';
import OverlayPartner from '../../containers/Widget/OverlayPartner';
import OverlayPublisher from '../Widget/OverlayPublisher';
import OverlayVoteSmart from '../Widget/OverlayVoteSmart';
import OverlayRepStance from '../Widget/OverlayRepStance';
import NextAction from '../Widget/NextAction';

import { isLongText } from '../../components/Shared/commonFunctions';
import FindLocalRepPopup from '../../components/LocalRep/FindLocalRepPopup';
import CssTransition from '../../components/Transition/CssTransition';
import ShareDrawer from '../../components/ShareDrawer';
import { LoadingSpinner } from '../../components/LoadingSpinner';

import { Placeholders } from '../../utils/fillDynamicText';
import { ThemeConsumer } from '../../utils/theming';

import {
    completeRepStance,
    getRepStances,
    initiateRepStance,
    repStanceUpdated
} from './actions';
import { triggerFindYourRep, hideFindRepPopup, loadLocalRep } from '../LocalRep/actions';
import {
    onGoToNextCard,
    setOverlayPartnerVisible,
    setOverlayRepStanceVisible,
    setExitingCardProps,
    getExitingCardProps
} from '../Widget/actions';

import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';
import { isPortrait } from '../../utils/size';
import { getActionCompletedTime } from '../../utils/actionTiming';
import { relevantRepresentatives } from '../../utils/localRepresentative/parser';
import { copyFor } from '../../copy/copy';

const RepLink = styled(FooterSpan)`
    position: absolute;
    top: 0;
    left: 0;

    @media (min-width: 24em) {
        display: none;
    }
`;

class RepStanceDetails extends React.Component {
    constructor() {
        super();
        this.state = {
            nextActionVisible: false
        };
    }

    static propTypes = {
        actionCompletedTime: PropTypes.func.isRequired,
        activeAction: PropTypes.object.isRequired,
        address: PropTypes.object.isRequired,
        apiRequestData: PropTypes.object.isRequired,
        completeRepStance: PropTypes.func.isRequired,
        country: PropTypes.string,
        endUserLocation: PropTypes.object.isRequired,
        getRepStances: PropTypes.func.isRequired,
        hideFindRepPopup: PropTypes.func.isRequired,
        initiateRepStance: PropTypes.func.isRequired,
        isLocalRepLoading: PropTypes.bool,
        isPortrait: PropTypes.bool.isRequired,
        loadLocalRep: PropTypes.func.isRequired,
        nextAction: PropTypes.object,
        nextCard: PropTypes.object,
        triggerFindYourRep: PropTypes.func.isRequired,
        onGoToNextCard: PropTypes.func.isRequired,
        popupErrorMessage: PropTypes.string,
        questionText: PropTypes.string,
        questionTextDisplay: PropTypes.string.isRequired,
        representativeSearch: PropTypes.objectOf(PropTypes.array),
        reps: PropTypes.arrayOf(PropTypes.object).isRequired,
        repStanceLoading: PropTypes.bool.isRequired,
        repStances: PropTypes.arrayOf(PropTypes.object).isRequired,
        repStanceUnavailable: PropTypes.bool.isRequired,
        repStanceUpdated: PropTypes.func.isRequired,
        sequenceAutomatically: PropTypes.bool.isRequired,
        setExitingCardProps: PropTypes.func.isRequired,
        setOverlayPartnerVisible: PropTypes.func.isRequired,
        setOverlayRepStanceVisible: PropTypes.func.isRequired,
        showFindYouRepPopup: PropTypes.bool.isRequired
    };

    componentDidMount = async () => {
        if (this.props.reps.length === 0 && !this.props.isLocalRepLoading) {
            await this.props.loadLocalRep(
                this.props.endUserLocation,
                undefined,
                this.props.representativeSearch
            );
        }
        if (this.props.repStances.length === 0 && !this.props.repStanceLoading) {
            await this.props.getRepStances(this.props.reps, this.props.questionText);
        }

        if (this.props.activeAction.skipFrontCard) {
            this.props.initiateRepStance(this.props.activeAction.identifier);
            this.props.completeRepStance(
                this.props.apiRequestData,
                this.props.actionCompletedTime(),
                this.props.activeAction.identifier
            );
        }

        if (!!this.props.nextAction) this.setState({ nextActionVisible: true });
    };

    handleGetDifferentReps = async () => {
        await this.props.loadLocalRep(
            this.props.endUserLocation,
            this.props.address,
            this.props.representativeSearch
        );
        this.props.getRepStances(this.props.reps, this.props.questionText);
    };

    handleCompleteAction = () => {
        this.props.setExitingCardProps(
            'checkrepposition',
            this.props,
            this.props.activeAction
        );
        if (!!this.props.nextAction) {
            this.props.onGoToNextCard(
                this.props.nextAction.identifier,
                this.props.nextCard.type,
                this.props.nextAction.type,
                this.props.nextAction.skipFrontCard ||
                    this.props.nextAction.scrollableDesign,
                false
            );
        }
    };

    render() {
        return (
            <ThemeConsumer>
                {theme => (
                    <Container>
                        <Overlay />
                        <OverlayPublisher />
                        <OverlayPartner transitionType="right-to-left" />
                        <OverlayVoteSmart transitionType="right-to-left" />
                        <OverlayRepStance
                            transitionType="right-to-left"
                            handleCompleteAction={this.handleCompleteAction}
                            nextAction={!!this.props.nextAction}
                        />
                        <Body isBlured={this.props.showFindYouRepPopup}>
                            <WidgetHeader />
                            <Content flexGrow>
                                <DetailsContainer>
                                    <Details>
                                        <Title
                                            scaleFactor={0.7}
                                            dangerouslySetInnerHTML={{
                                                __html: this.props.questionTextDisplay
                                            }}
                                            isLongText={isLongText(
                                                this.props.questionTextDisplay,
                                                65
                                            )}
                                            isExtraLongText={isLongText(
                                                this.props.questionTextDisplay,
                                                115
                                            )}
                                            onClick={e =>
                                                this.props.setOverlayPartnerVisible(
                                                    true,
                                                    e,
                                                    false,
                                                    false,
                                                    false
                                                )
                                            }
                                        />
                                        <RepsWrapper
                                            className="ie-rep-stance-container"
                                            repStanceLoading={this.props.repStanceLoading}
                                            nextActionVisible={
                                                this.state.nextActionVisible
                                            }
                                            isPortrait={this.props.isPortrait}
                                        >
                                            <RepLink
                                                style={{ top: `-10px` }}
                                                className={'findRep'}
                                            >
                                                {copyFor('findReps', 'notYour')}
                                                <span
                                                    onClick={e => {
                                                        e.preventDefault();
                                                        this.props.triggerFindYourRep();
                                                    }}
                                                >
                                                    {' '}
                                                    {copyFor(
                                                        'findReps',
                                                        'clickToFindShort'
                                                    )}
                                                </span>
                                            </RepLink>
                                            {this.props.repStanceLoading &&
                                            !!this.props.repStances ? (
                                                <LoadingSpinner
                                                    position={'center'}
                                                    width={80}
                                                    height={80}
                                                    stroke={6}
                                                    color={theme.colors.primary}
                                                />
                                            ) : (
                                                this.props.repStances.map(rep => {
                                                    return (
                                                        <RepPosition
                                                            theme={theme}
                                                            nextActionVisible={
                                                                this.state
                                                                    .nextActionVisible
                                                            }
                                                            isPortrait={
                                                                this.props.isPortrait
                                                            }
                                                            key={rep.fullName}
                                                            answer={rep.answer}
                                                            answerType={rep.answerType}
                                                            repName={rep.displayName}
                                                            src={
                                                                rep.photoUrl ||
                                                                DefaultRepImageURL(
                                                                    this.props.country
                                                                )
                                                            }
                                                            handleClick={() => {
                                                                this.props.repStanceUpdated(
                                                                    this.props
                                                                        .activeAction
                                                                        .identifier
                                                                );
                                                                this.props.setOverlayRepStanceVisible(
                                                                    true,
                                                                    rep.voteSmartCandidateId
                                                                );
                                                            }}
                                                        />
                                                    );
                                                })
                                            )}
                                        </RepsWrapper>
                                    </Details>
                                </DetailsContainer>
                            </Content>
                            {this.state.nextActionVisible && (
                                <NextAction
                                    noCountdown={!this.props.sequenceAutomatically}
                                    beforeAdvancing={() => {
                                        this.props.setExitingCardProps(
                                            'checkrepposition',
                                            this.props,
                                            this.props.activeAction
                                        );
                                    }}
                                    exitingAction={this.props.activeAction}
                                />
                            )}
                            <Footer>
                                <ShareDrawer
                                    beforeBack={() => {
                                        this.props.setExitingCardProps(
                                            'checkrepposition',
                                            this.props,
                                            this.props.activeAction
                                        );
                                    }}
                                    multipleReps={this.props.reps.length > 1}
                                    newsletterSignUpVisible={false}
                                    fullwidth={true}
                                    backgroundColor={'#fff'}
                                />
                            </Footer>
                        </Body>
                        <TransitionGroup>
                            {this.props.showFindYouRepPopup && (
                                <CssTransition
                                    key="alert"
                                    in={this.props.showFindYouRepPopup}
                                >
                                    <FindLocalRepPopup
                                        noBack={this.props.reps.length === 0}
                                        message={
                                            this.props.popupErrorMessage &&
                                            this.props.repStanceUnavailable
                                                ? this.props.popupErrorMessage
                                                : null
                                        }
                                        address={this.props.address}
                                        submitting={this.props.isLocalRepLoading}
                                        hideFindRepPopup={this.props.hideFindRepPopup}
                                        onSubmit={() => this.handleGetDifferentReps()}
                                    />
                                </CssTransition>
                            )}
                        </TransitionGroup>
                    </Container>
                )}
            </ThemeConsumer>
        );
    }
}

export default connect(
    (state, props) => {
        const exitingProps = getExitingCardProps(
            'checkrepposition',
            ['/checkrepposition'],
            props.location.pathname,
            state
        );
        if (exitingProps) return exitingProps;

        const representativeSearch = state.widget.maximumRepresentativeSearch;
        const defaultRepSearch = state.repStance.defaultRepSearch;
        const reps = state.localRep.localRepData
            ? relevantRepresentatives(
                  defaultRepSearch,
                  state.localRep.localRepData.representative
              )
            : [];
        const country = ((state.localRep.localRepData || {}).address || {}).country;
        const nextAction = getNextAction(
            state.widget.actions,
            state.widget.activeAction.identifier,
            buildConditionalActionsAnswerHistory(state),
            state.widget.cards
        );
        return {
            actionCompletedTime: getActionCompletedTime(state, 'repStance'),
            activeAction: state.widget.activeAction,
            address: state.form.google_autocomplete
                ? state.form.google_autocomplete.values
                : {},
            apiRequestData: state.widget.apiRequestData,
            country,
            endUserLocation: state.widget.endUserLocation,
            popupErrorMessage: state.repStance.popupErrorMessage,
            repStanceUnavailable: state.repStance.repStanceUnavailable,
            isPortrait: isPortrait(),
            isLocalRepLoading: state.localRep.isLocalRepLoading,
            nextAction,
            nextCard: nextAction ? state.widget.cards[nextAction.order - 1] : null,
            questionText: state.widget.activeAction.questionText,
            questionTextDisplay: (
                state.widget.activeAction.customQuestionText || Placeholders.Question
            ).replace(Placeholders.Question, state.widget.activeAction.questionText),
            reps,
            representativeSearch,
            repStanceLoading: state.repStance.repStanceLoading,
            repStances: state.repStance.stances,
            sequenceAutomatically: state.widget.activeAction.sequenceAutomatically,
            showFindYouRepPopup: state.localRep.showFindYouRepPopup
        };
    },
    {
        completeRepStance,
        getRepStances,
        hideFindRepPopup,
        initiateRepStance,
        loadLocalRep,
        triggerFindYourRep,
        onGoToNextCard,
        repStanceUpdated,
        setExitingCardProps,
        setOverlayPartnerVisible,
        setOverlayRepStanceVisible
    }
)(RepStanceDetails);
